// Third-party libraries
import React, { Fragment, useState } from "react";
import { intComma, pluralize } from "humanize-plus";

// Project dependencies
import { LoadingSpinner } from "./widgets";
import { oddsToString, payoutToOdds } from "../utilities";
import RibbonIcon from "../svg/RibbonIcon";
import TrendUpIcon from "../svg/TrendUpIcon";
import TrendDownIcon from "../svg/TrendDownIcon";
import { Bet, ProposedBet, Proposition } from "../shared/interfaces";
import variables from "../styleVariables";

interface Props {
  proposedTotal: number;
  existingTotal: number;
  option: any;
  loggedIn: boolean;
  proposedBet: ProposedBet;
  makeSingleBet: Function;
  availablePoints: number;
  proposition: Proposition;
  handleChange: Function;
  loading: boolean;
  resetBet: Function;
  addToBet: Function;
  existingBets: Array<Bet> | null;
}

function OptionComponent({
  addToBet,
  existingBets,
  existingTotal,
  handleChange,
  loading,
  loggedIn,
  makeSingleBet,
  option,
  availablePoints,
  proposedBet,
  proposedTotal,
  proposition,
  resetBet
}: Props) {
  return (
    <div
      key={option.id}
      className={`option ${IsWinner(proposition, option.option) &&
        "option--winner"}`}
    >
      <div className="option__odds">
        {loading ? <LoadingSpinner /> : oddsToString(option.odds)}
      </div>

      <label
        className={"option__name option__main"}
        htmlFor={`option-${option.id}`}
      >
        {IsWinner(proposition, option.option) && (
          <RibbonIcon color={variables.colorPrimary} />
        )}{" "}
        {option.option.name}
        {((option.payoutTrend > 0.15 || option.rankTrend > 0.25) &&
          proposition.coming && <TrendUpIcon />) ||
          ((option.payoutTrend < -0.3 || option.rankTrend < -0.25) &&
            proposition.coming && <TrendDownIcon />)}
        {option.option.team && (
          <div className="option__team">{option.option.team}</div>
        )}
        {loggedIn && existingBets && <OpenBets bets={existingBets} />}
        {loggedIn && existingBets && <Resolution bets={existingBets} />}
        {loggedIn &&
          (existingBets || proposedBet.amount > 0) &&
          proposition.status === 2 && (
            <div
              className={`option__potential-net ${
                proposedTotal > 0 ? "option__potential-net--theoretical" : ""
              }`}
            >
              Your potential net:{" "}
              {intComma(
                potentialProfit(
                  existingBets,
                  existingTotal,
                  proposedTotal,
                  proposedBet.amount,
                  option.odds
                )
              )}
            </div>
          )}
      </label>

      {loggedIn && proposition.coming && (
        <BetInput
          addToBet={addToBet}
          availablePoints={availablePoints}
          handleChange={handleChange}
          loading={loading}
          makeSingleBet={makeSingleBet}
          proposedBet={proposedBet}
          resetBet={resetBet}
        />
      )}
    </div>
  );
}

function OpenBets({ bets }: { bets: Array<Bet> }) {
  const [showAllBets, updateShowAllBets] = useState(false);
  const INITIAL_REVEAL = 6;
  if (bets.length === 0) {
    return null;
  }
  return (
    <Fragment>
      <div className="option__existing-bet">
        Your {`${pluralize(bets.length, "bet")}:`}
      </div>

      <ul className="option__existing-bet-list">
        {bets
          .sort((a, b) => b.amount - a.amount)
          .slice(
            0,
            showAllBets || bets.length <= INITIAL_REVEAL
              ? bets.length
              : INITIAL_REVEAL - 2
          )
          .map(bet => (
              <li key={bet.id} className="option__existing-bet">
                <span className="option__existing-bet-amount">
                  {intComma(bet.amount)}
                </span>
                <span className="option__existing-bet-odds">
                  {oddsToString(payoutToOdds(bet.pre_payout))}
                </span>
              </li>
            ))}
      </ul>
      {!showAllBets && bets.length > INITIAL_REVEAL && (
        <button
          className="button--inline option__existing-bet option__show-all-bets"
          onClick={() => updateShowAllBets(true)}
        >
          … and {bets.length - INITIAL_REVEAL + 2} more
        </button>
      )}
    </Fragment>
  );
}

function Resolution({ bets }: { bets: Array<any> }) {
  if (bets.length === 0) {
    return null;
  }
  if (bets[0].status === 5) {
    const profit = bets.reduce((a, b) => a + b.profit, 0);
    return (
      <div className="option__existing-bet">{`You profited ${intComma(
        profit
      )} ${pluralize(profit, "point")} on this option.`}</div>
    );
  } else if (bets[0].status === 4) {
    const loss = bets.reduce((a, b) => a + b.amount, 0);
    return (
      <div className="option__existing-bet">{`You lost ${intComma(
        loss
      )} ${pluralize(loss, "point")} on this option.`}</div>
    );
  } else {
    return null;
  }
}

function potentialProfit(
  existingBets: Array<Bet> | null,
  existingTotal: number,
  proposedTotal: number,
  theoreticalBet: number,
  currentOdds: [number, number]
): number {
  if (!existingBets) {
    return 0;
  }
  let gross: number = 0;

  existingBets.map(bet => {
    const odds = payoutToOdds(bet.pre_payout);
    gross += (bet.amount * odds[0]) / odds[1] + bet.amount;
  });

  gross += (theoreticalBet * currentOdds[0]) / currentOdds[1] + theoreticalBet;

  return gross - existingTotal - proposedTotal;
}

function IsWinner(proposition, option) {
  if (proposition.status !== 4) {
    return false;
  } else if (proposition.winner && proposition.winner.id === option.id) {
    return true;
  } else if (proposition.winner2 && proposition.winner2.id === option.id) {
    return true;
  } else if (proposition.winner3 && proposition.winner3.id === option.id) {
    return true;
  } else {
    return false;
  }
}

function BetInput({
  proposedBet,
  handleChange,
  loading,
  makeSingleBet,
  addToBet,
  resetBet
}: any) {
  return loading ? (
    <div className="bet-input">
      <LoadingSpinner />
    </div>
  ) : (
    <div className="bet-input">
      <input
        type="number"
        size={5}
        pattern="[0-9,]*"
        value={proposedBet.amount > 0 ? proposedBet.amount : ""}
        name={proposedBet.option_data_id}
        onChange={handleChange}
        id={`option-${proposedBet.option_data_id}`}
      />{" "}
      <div className="bet-input__modifiers">
        <button
          tabIndex={-1}
          className="bet-input__modifier"
          type="button"
          onClick={addToBet()}
          value={`10,${proposedBet.option_data_id}`}
        >
          +10
        </button>

        <button
          tabIndex={-1}
          className="bet-input__modifier"
          type="button"
          onClick={addToBet()}
          value={`25,${proposedBet.option_data_id}`}
        >
          +25
        </button>

        <button
          tabIndex={-1}
          className="bet-input__modifier bet-input__modifier--clear"
          type="button"
          onClick={resetBet()}
          value={proposedBet.option_data_id}
          disabled={proposedBet.amount === 0}
        >
          X
        </button>
        <button
          tabIndex={-1}
          className="bet-input__modifier bet-input__modifier--bet"
          type="button"
          onClick={makeSingleBet()}
          value={proposedBet.option_data_id}
          disabled={proposedBet.amount === 0}
        >
          Bet
        </button>
      </div>
    </div>
  );
}

export default OptionComponent;
